import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useUserOrganization } from './useUserOrganization'

/**
 * Fetches excluded pickup code prefixes from the database.
 * Used to determine when to auto-search for pickup codes.
 *
 * Example: If excluded = ['6', '7'], then:
 * - Input "4821" → auto-search pickup code
 * - Input "6821" → wait (could be typing 682134 order number)
 * - Input "721234" → search order number
 */
export function useExcludedPickupPrefixes() {
  return useQuery({
    queryKey: ['excluded-pickup-prefixes'],
    queryFn: async () => {
      const { data, error } = await supabase
        .schema('delivery')
        .rpc('get_excluded_pickup_prefixes')

      if (error) throw error
      return data as string[]
    },
    staleTime: 1000 * 60 * 60, // Cache for 1 hour (rarely changes)
  })
}

/**
 * Searches for an order by pickup code.
 */
export function useOrderByPickupCode(code: string | null) {
  const { data: membership } = useUserOrganization()

  return useQuery({
    queryKey: ['order-by-pickup-code', code],
    queryFn: async () => {
      if (!code || !membership?.organization_id) return null

      const { data, error } = await supabase
        .schema('delivery')
        .rpc('get_order_by_pickup_code', {
          p_organization_id: membership.organization_id,
          p_pickup_code: code,
        })

      if (error) throw error
      return data?.[0] ?? null
    },
    enabled: !!code && code.length === 4 && !!membership?.organization_id,
  })
}

/**
 * Determines if a 4-digit input should trigger auto-search for pickup code.
 *
 * @param input - The user input
 * @param excludedPrefixes - Prefixes that could be order numbers (e.g., ['6', '7'])
 * @returns true if should auto-search, false if should wait for more input
 */
export function shouldAutoSearchPickupCode(
  input: string,
  excludedPrefixes: string[]
): boolean {
  // Must be exactly 4 digits
  if (!/^\d{4}$/.test(input)) return false

  // First digit must NOT be in excluded list
  const firstDigit = input[0]
  return !excludedPrefixes.includes(firstDigit)
}
